// lib/locales/index.ts
// Configuración central de internacionalización

import {
  SUPPORTED_LOCALES,
  DEFAULT_LOCALE,
  FALLBACK_LOCALE,
  I18N_NAMESPACES,
  type SupportedLocaleCode,
  type LocaleConfig,
} from "../../convex/lib/config";

// ============================================================================
// RE-EXPORTS FROM CONFIG
// ============================================================================

export {
  SUPPORTED_LOCALES,
  DEFAULT_LOCALE,
  FALLBACK_LOCALE,
  I18N_NAMESPACES,
  getLocaleConfig,
  isSupportedLocale,
  getValidLocale,
  getLocaleOptions,
} from "../../convex/lib/config";

export type { SupportedLocaleCode, LocaleConfig, I18nNamespace } from "../../convex/lib/config";

// ============================================================================
// LOCALE RESOURCES
// ============================================================================

// Español
import esCommon from "./es/common.json";
import esLogistics from "./es/logistics.json";
import esClients from "./es/clients.json";
import esNavigation from "./es/navigation.json";
import esReports from "../../convex/lib/i18n/locales/es/reports.json";

// English
import enCommon from "./en/common.json";
import enLogistics from "./en/logistics.json";
import enClients from "./en/clients.json";
import enNavigation from "./en/navigation.json";
import enReports from "../../convex/lib/i18n/locales/en/reports.json";

/**
 * Recursos de traducción organizados por idioma y namespace
 */
export const resources = {
  es: {
    common: esCommon,
    logistics: esLogistics,
    clients: esClients,
    navigation: esNavigation,
    reports: esReports,
    // Agregar más namespaces aquí
    // system: esSystem,
    // security: esSecurity,
    // subscriptions: esSubscriptions,
    // notifications: esNotifications,
  },
  en: {
    common: enCommon,
    logistics: enLogistics,
    clients: enClients,
    navigation: enNavigation,
    reports: enReports,
    // Agregar más namespaces aquí
    // system: enSystem,
    // security: enSecurity,
    // subscriptions: enSubscriptions,
    // notifications: enNotifications,
  },
} as const;

/**
 * Tipo de recursos disponibles
 */
export type Resources = typeof resources;

/**
 * Namespaces disponibles (extraídos de los recursos)
 */
export type AvailableNamespace = keyof (typeof resources)["es"];

// ============================================================================
// NAMESPACE CONFIGURATION
// ============================================================================

/**
 * Lista de todos los namespaces disponibles
 */
export const availableNamespaces: AvailableNamespace[] = Object.keys(
  resources[DEFAULT_LOCALE]
) as AvailableNamespace[];

/**
 * Namespace por defecto (common)
 */
export const defaultNamespace: AvailableNamespace = "common";

// ============================================================================
// I18N CONFIGURATION FOR LIBRARIES
// ============================================================================

/**
 * Configuración para react-i18next
 */
export const i18nextConfig = {
  resources,
  lng: DEFAULT_LOCALE,
  fallbackLng: FALLBACK_LOCALE,
  defaultNS: defaultNamespace,
  ns: availableNamespaces,
  
  interpolation: {
    escapeValue: false, // React already escapes
  },
  
  // Opciones de detección de idioma
  detection: {
    order: ["localStorage", "navigator"],
    lookupLocalStorage: "i18nextLng",
    caches: ["localStorage"],
  },
  
  // Opciones de react
  react: {
    useSuspense: true,
  },
};

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

/**
 * Obtiene el idioma del navegador si está soportado
 */
export function getBrowserLocale(): SupportedLocaleCode {
  if (typeof navigator === "undefined") {
    return DEFAULT_LOCALE;
  }
  
  const browserLang = navigator.language.split("-")[0];
  const supportedCodes = SUPPORTED_LOCALES.map((l) => l.code);
  
  if (supportedCodes.includes(browserLang as SupportedLocaleCode)) {
    return browserLang as SupportedLocaleCode;
  }
  
  return DEFAULT_LOCALE;
}

/**
 * Obtiene el idioma guardado en localStorage
 */
export function getStoredLocale(): SupportedLocaleCode | null {
  if (typeof localStorage === "undefined") {
    return null;
  }
  
  const stored = localStorage.getItem("i18nextLng");
  if (stored && SUPPORTED_LOCALES.some((l) => l.code === stored)) {
    return stored as SupportedLocaleCode;
  }
  
  return null;
}

/**
 * Guarda el idioma preferido en localStorage
 */
export function setStoredLocale(locale: SupportedLocaleCode): void {
  if (typeof localStorage !== "undefined") {
    localStorage.setItem("i18nextLng", locale);
  }
}

/**
 * Obtiene el mejor idioma disponible (guardado > navegador > default)
 */
export function getPreferredLocale(): SupportedLocaleCode {
  return getStoredLocale() ?? getBrowserLocale();
}

// ============================================================================
// TYPE DECLARATIONS FOR TYPE-SAFE TRANSLATIONS
// ============================================================================

/**
 * Declaración para mejorar el tipado de useTranslation
 * Requiere configuración adicional en el proyecto
 */
declare module "react-i18next" {
  interface CustomTypeOptions {
    defaultNS: typeof defaultNamespace;
    resources: Resources[typeof DEFAULT_LOCALE];
  }
}
